import { AiChatState } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { aiApiClient } from "@src/api/ai-api";
import { queryOptions, useQuery } from "@tanstack/react-query";

interface UseGetChatHistoryOptions {
  sessionId: string | null;
  isAdmin?: boolean;
  enabled?: boolean;
}

/**
 * The saved transcript of one chat session. Exported for callers that need
 * the server's CURRENT state outside a render — resuming an interrupted turn
 * re-reads it with `queryClient.fetchQuery` rather than trusting the copy the
 * page loaded with.
 */
export const chatHistoryQueryOptions = ({
  sessionId,
  isAdmin = false,
}: Pick<UseGetChatHistoryOptions, "sessionId" | "isAdmin">) =>
  queryOptions({
    queryKey: ["chat-history", sessionId, isAdmin ? "admin" : "user"],
    queryFn: async () => {
      if (!sessionId) {
        throw new Error("Session ID is required");
      }

      // The user endpoint answers with the chat state itself; the admin one
      // wraps the same state as `{ chat }`. Callers get the one shape.
      if (isAdmin) {
        const response = await aiApiClient.get<{
          chat: AiChatState | null;
        } | null>(`/chat-history/admin/session/${sessionId}`);
        return response.data?.chat ?? null;
      }

      const response = await aiApiClient.get<AiChatState | null>(
        `/chat-history/session/${sessionId}`
      );
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

export const useGetChatHistory = ({
  sessionId,
  isAdmin = false,
  enabled = true,
}: UseGetChatHistoryOptions) => {
  return useQuery({
    ...chatHistoryQueryOptions({ sessionId, isAdmin }),
    enabled: enabled && !!sessionId,
  });
};
